import Text from "components/atoms/Text";
import { Button, COLOR_SYSTEM, Flex, Input } from "components/atoms";
import { useEffect, useState } from "react";
import { LogLevel, log } from "utils/LogUtil";

interface PasswordErrors {
  emptyCurrentPassword: boolean;
  emptyNewPassword: boolean;
  emptyConfirmPassword: boolean;
  shortPassword: boolean;
  notEqualPassword: boolean;
  invalidCurrentPassword: boolean;
}

interface Props {
  onClose: () => void;
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>;
  isLoading: boolean;
  errors: PasswordErrors;
  onInputChange: (field: string, value: string) => void;
}

const initErrors: PasswordErrors = {
  emptyCurrentPassword: false,
  emptyNewPassword: false,
  emptyConfirmPassword: false,
  shortPassword: false,
  notEqualPassword: false,
  invalidCurrentPassword: false,
};

const ChangePassword: React.FC<Props> = ({
  onClose,
  onSubmit,
  isLoading,
  errors,
  onInputChange,
}) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localErrors, setLocalErrors] = useState<PasswordErrors>(initErrors);

  log(LogLevel.VERBOSE, "ChangePassword: render");

  useEffect(() => {
    log(LogLevel.VERBOSE, "ChangePassword: useEffect", errors);
  }, [errors]);

  const validateForm = (): boolean => {
    const newErrors = { ...initErrors };
    let isValid = true;

    if (!currentPassword) {
      newErrors.emptyCurrentPassword = true;
      isValid = false;
    }

    if (!newPassword) {
      newErrors.emptyNewPassword = true;
      isValid = false;
    } else if (newPassword.length < 8) {
      newErrors.shortPassword = true;
      isValid = false;
    }

    if (!confirmPassword) {
      newErrors.emptyConfirmPassword = true;
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      newErrors.notEqualPassword = true;
      isValid = false;
    }

    setLocalErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    await onSubmit(currentPassword, newPassword);
  };

  const handleInputChange = (field: string, value: string) => {
    setLocalErrors((prev) => {
      const newErrors = { ...prev };
      switch (field) {
        case "currentPassword":
          newErrors.emptyCurrentPassword = false;
          break;
        case "newPassword":
          newErrors.emptyNewPassword = false;
          newErrors.shortPassword = false;
          break;
        case "confirmPassword":
          newErrors.emptyConfirmPassword = false;
          newErrors.notEqualPassword = false;
          break;
      }
      return newErrors;
    });
    onInputChange(field, value);
  };

  const renderErrorMessage = (field: keyof PasswordErrors): string => {
    // 서버에서 오는 에러가 우선
    if (errors[field]) {
      switch (field) {
        case "invalidCurrentPassword":
          return "현재 비밀번호가 일치하지 않습니다.";
        default:
          return "";
      }
    }

    // 로컬 유효성 검사 에러
    if (localErrors[field]) {
      switch (field) {
        case "emptyCurrentPassword":
          return "현재 비밀번호를 입력해주세요.";
        case "emptyNewPassword":
          return "새 비밀번호를 입력해주세요.";
        case "emptyConfirmPassword":
          return "비밀번호 확인을 입력해주세요.";
        case "shortPassword":
          return "비밀번호는 8자 이상이어야 합니다.";
        case "notEqualPassword":
          return "비밀번호가 일치하지 않습니다.";
        default:
          return "";
      }
    }
    return "";
  };

  log(LogLevel.VERBOSE, "ChangePassword: render", localErrors, errors);
  return (
    <Flex
      direction="column"
      justifyContent="space-between"
      customStyle={{ width: "100%", height: "100%" }}
    >
      <Flex direction="column" gap="40px">
        <Flex direction="column" gap="20px" customStyle={{ marginTop: "20px" }}>
          <Text textType={"H1"} color={COLOR_SYSTEM.get("Gray")[900]}>
            비밀번호 변경
          </Text>
        </Flex>
        <Flex direction="column" gap="16px">
          <Input
            label="현재 비밀번호"
            type="password"
            required
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              handleInputChange("currentPassword", e.target.value);
            }}
            error={localErrors.emptyCurrentPassword || errors.invalidCurrentPassword}
            hint={renderErrorMessage(
              localErrors.emptyCurrentPassword ? "emptyCurrentPassword" : "invalidCurrentPassword"
            )}
          />
          <Input
            label="새 비밀번호"
            type="password"
            required
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              handleInputChange("newPassword", e.target.value);
            }}
            error={localErrors.emptyNewPassword || localErrors.shortPassword}
            hint={renderErrorMessage(
              localErrors.emptyNewPassword ? "emptyNewPassword" : "shortPassword"
            )}
          />
          <Input
            label="새 비밀번호 확인"
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              handleInputChange("confirmPassword", e.target.value);
            }}
            error={localErrors.emptyConfirmPassword || localErrors.notEqualPassword}
            hint={renderErrorMessage(
              localErrors.emptyConfirmPassword ? "emptyConfirmPassword" : "notEqualPassword"
            )}
          />
        </Flex>
      </Flex>

      <Flex gap="12px" customStyle={{ marginTop: "40px" }}>
        <Button
          size={"Large"}
          type={"Text"}
          variant={"Tinted"}
          color={"Secondary"}
          style={{ flex: 1 }}
          onClick={onClose}
          disabled={isLoading}
        >
          취소
        </Button>
        <Button
          size={"Large"}
          type={"Text"}
          variant={"Contained"}
          color={"Primary"}
          style={{ flex: 1 }}
          onClick={handleSubmit}
          disabled={isLoading}
        >
          변경하기
        </Button>
      </Flex>
    </Flex>
  );
};

export default ChangePassword;
